import {
  CustomFieldProfile,
  useCreateCustomFieldProfileMutation,
  useDeleteCustomFieldProfileMutation,
  useUpdateCustomFieldProfileMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseCustomFieldProfileMutationProps = {}

export type UseCustomFieldProfileMutationResult = {
  createCustomFieldProfile: (customFieldProfile: Partial<CustomFieldProfile>, callback: EntityMutationCallback<CustomFieldProfile> | undefined) => void
  updateCustomFieldProfile: (customFieldProfile: Partial<CustomFieldProfile>, callback: EntityMutationCallback<CustomFieldProfile> | undefined) => void
  deleteCustomFieldProfile: (customFieldProfileId: string, callback: EntityMutationCallback | undefined) => void



  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useCustomFieldProfileMutation = (props: UseCustomFieldProfileMutationProps = {}): UseCustomFieldProfileMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createCustomFieldProfile, {isLoading: isCreating}] = useCreateCustomFieldProfileMutation()
  const [_updateCustomFieldProfile, {isLoading: isUpdating}] = useUpdateCustomFieldProfileMutation()
  const [_deleteCustomFieldProfile, {isLoading: isDeleting}] = useDeleteCustomFieldProfileMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useCustomFieldProfileUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.customfieldprofile", "Custom Field Profile"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useCustomFieldProfileUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useCustomFieldProfileUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.customfieldprofile", "Custom Field Profile"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useCustomFieldProfileUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useCustomFieldProfileUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.customfieldprofile", "Custom Field Profile"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useCustomFieldProfileUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<CustomFieldProfile>, callback: EntityMutationCallback<CustomFieldProfile> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.customfieldprofile", "Custom Field Profile") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<CustomFieldProfile>, callback: EntityMutationCallback<CustomFieldProfile> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.customfieldprofile", "Custom Field Profile") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.customfieldprofile", "Custom Field Profile") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((customFieldProfileId: string, callback: EntityMutationCallback<CustomFieldProfile> | undefined) => {
    const promise = _deleteCustomFieldProfile({id: customFieldProfileId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteCustomFieldProfile, handleDeleteResult])

  const createCustomFieldProfile = useCallback((customFieldProfile: Partial<CustomFieldProfile>, callback: EntityMutationCallback<CustomFieldProfile> | undefined) => {
    const promise = _createCustomFieldProfile(customFieldProfile).unwrap()
    handleCreateResult(promise, callback)
  }, [_createCustomFieldProfile, handleCreateResult])

  const updateCustomFieldProfile = useCallback((customFieldProfile: Partial<CustomFieldProfile>, callback: EntityMutationCallback | undefined) => {
    const promise = _updateCustomFieldProfile(customFieldProfile).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateCustomFieldProfile, handleUpdateResult])

  const deleteCustomFieldProfile = useCallback((customFieldProfileId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.customfieldprofile", "Custom Field Profile"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.customFieldProfile.confirm", "Are you sure you want to delete this custom field profile?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(customFieldProfileId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createCustomFieldProfile: createCustomFieldProfile,
    updateCustomFieldProfile: updateCustomFieldProfile,
    deleteCustomFieldProfile: deleteCustomFieldProfile,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
